import { EMetricSystem } from '@models/EMetricSystem';
import { SurveyResultType, SurveyStatusType } from '@models/Surveys';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SitesShowFilter } from '@shared/components/Maps/Togglers';
import { subDays } from 'date-fns';

export interface MapFilters {
  showLNS: boolean;
  showCSQ: boolean;
  showName: boolean;
  showOption: SitesShowFilter;
  showGIS: boolean;
  showGISPipeData: boolean;
  showMeasurementTool: boolean;
  showGISPipeInformation: boolean;
  gisPipeMetricSystem: EMetricSystem;
  showGISAssetsData: boolean;
  showGISAssetIds: boolean;
  showGISDMAData: boolean;
  showCorrelations: boolean;
  dateRange: [Date, Date];
  showHighConfidenceOnly: boolean;
  showGISCorrelations: boolean;
  showLinkedSites: boolean;
  showSurveyMeasurements: boolean;
  showPRVLogger: boolean;
  showFlowLogger: boolean;
  showPressureLogger: boolean;
  showFlowPressureLogger: boolean;
  showSitesWithoutLocation: boolean;
  showThresholdModelling: boolean;
  thresholdModelling: number; // range 8-20
  showSurveys: boolean;
  surveysDateRange: [Date, Date];
  surveysStatus: SurveyStatusType | null;
  surveysResult: SurveyResultType | null;
}

export const DefaultMapFilters: MapFilters = {
  showLNS: false,
  showCSQ: false,
  showName: false,
  showOption: SitesShowFilter.ShowAllSites,
  showGIS: true,
  showGISPipeData: false,
  showGISPipeInformation: false,
  gisPipeMetricSystem: EMetricSystem.Metric,
  showGISAssetsData: false,
  showGISAssetIds: false,
  showGISDMAData: false,
  showCorrelations: false,
  dateRange: [null, null],
  showHighConfidenceOnly: false,
  showGISCorrelations: false,
  showLinkedSites: false,
  showSurveyMeasurements: false,
  showPRVLogger: false,
  showFlowLogger: false,
  showPressureLogger: false,
  showFlowPressureLogger: false,
  showSitesWithoutLocation: false,
  showMeasurementTool: false,
  showThresholdModelling: false,
  thresholdModelling: 15,
  showSurveys: false,
  surveysDateRange: [subDays(new Date(), 7), new Date()],
  surveysStatus: null,
  surveysResult: null,
};

export const mapSlice = createSlice({
  name: 'map',
  initialState: {
    togglers: false,
    correlateSitesMode: false,
    filters: DefaultMapFilters,
    editedSiteLatLng: false,
  },
  reducers: {
    setMapTogglers(state, action: PayloadAction<boolean>) {
      state.togglers = action.payload;
    },
    setMapFilters(state, action: PayloadAction<Partial<MapFilters>>) {
      state.filters = { ...state.filters, ...action.payload };
    },
    setDisplayCorrelateSites(state, action: PayloadAction<boolean>) {
      state.correlateSitesMode = action.payload;
    },
    setEditedSiteLatLng(state, action: PayloadAction<boolean>) {
      state.editedSiteLatLng = action.payload;
    },
  },
});

export type MapState = ReturnType<typeof mapSlice.reducer>;

export default mapSlice;

export const { setMapTogglers, setMapFilters, setDisplayCorrelateSites, setEditedSiteLatLng } = mapSlice.actions;
